import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  FormOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { ApiService, TemplateDocument, TemplateSetup } from "../../api";
import SetupProgress, { Step } from "./SetupProgress";
import { designerFormUrl } from "../../constants";
import { useRefreshOnTabFocus, useTabTitle } from "../../tabs/tabContext";

import { PageHeader } from "../../components/PageHeader";

const { Text } = Typography;

/**
 * Everything it takes to turn a template into something people can raise
 * permits against.
 *
 * Until now this existed only as Python: templates were built by management
 * commands, so nobody outside the development team could make one. The work
 * genuinely spans three tools — details here, fields in the form designer,
 * order and approvals in the process designer — and the point of this page is
 * to make that one journey with a visible state, rather than three screens you
 * have to know about.
 */
const TemplateSetupPage = () => {
  const { t } = useTranslation();
  const { shortName } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [setup, setSetup] = useState<TemplateSetup | undefined>();
  const [sections, setSections] = useState<any[]>([]);
  const [documents, setDocuments] = useState<TemplateDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsForm] = Form.useForm();
  // The upload modal doubles as the rename/replace dialog; `editing` is the row
  // being changed, or null when a new document is being added.
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [editing, setEditing] = useState<TemplateDocument | null>(null);
  const [savingDocument, setSavingDocument] = useState(false);
  const [documentForm] = Form.useForm();

  const step = params.get("step") ?? "details";
  const setStep = (key: string) => setParams({ step: key }, { replace: true });

  useTabTitle(setup?.template?.name);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ApiService.apiMainTemplateSetupRetrieve(shortName!);
      setSetup(data);
      detailsForm.setFieldsValue({
        name: data.template.name,
        view_type: data.template.view_type,
      });
      const forms = await ApiService.apiMainFormsList();
      setSections(
        (forms as any[]).filter((f) => f.template === data.template.id)
      );
      setDocuments(
        await ApiService.apiMainTemplateDocumentsList(data.template.id)
      );
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortName]);

  useEffect(() => {
    load();
  }, [load]);

  // Sections are created in the form designer and stages in the process
  // designer, both in other tabs — so coming back here must re-read.
  useRefreshOnTabFocus(load);

  if (loading && !setup) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!setup) {
    return (
      <Alert type="error" showIcon message={t("This template could not be loaded.")} />
    );
  }

  const { counts, template, published_version: publishedVersion } = setup;
  const unplaced = counts.sections - counts.sections_in_a_stage;

  const steps: Step[] = [
    {
      key: "details",
      label: "Details",
      detail: template.template_type_name ?? t("No type"),
      done: !!template.name && !!template.template_type,
    },
    {
      key: "forms",
      label: "Sections",
      detail:
        counts.sections === 0
          ? t("None yet")
          : t("{{count}} sections", { count: counts.sections }),
      done: counts.sections > 0,
    },
    {
      key: "documents",
      label: "Documents",
      detail:
        counts.documents === 0
          ? t("None yet")
          : t("{{count}} documents", { count: counts.documents }),
      // Not every template prints anything, so this step is informational
      // rather than a gate — but it is done as soon as one document exists.
      done: counts.documents > 0,
    },
    {
      key: "process",
      label: "Process",
      detail:
        counts.stages === 0
          ? t("No stages")
          : t("{{count}} stages", { count: counts.stages }),
      done: counts.stages > 0,
    },
    {
      key: "publish",
      label: "Publish",
      detail:
        publishedVersion == null
          ? t("Not published")
          : `${t("Version")} ${publishedVersion}`,
      done: publishedVersion != null,
    },
  ];

  const saveDetails = async () => {
    const values = await detailsForm.validateFields();
    setSavingDetails(true);
    try {
      await ApiService.apiMainTemplatesPartialUpdate(shortName!, values as any);
      message.success(t("Saved"));
      await load();
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
    setSavingDetails(false);
  };

  const detailsPanel = (
    <Form form={detailsForm} layout="vertical" style={{ maxWidth: 520 }}>
      <Form.Item
        name="name"
        label={t("Name")}
        rules={[{ required: true, message: t("A name is required.") }]}
      >
        <Input placeholder={t("e.g. Hot Work Permit")} />
      </Form.Item>

      <Form.Item label={t("Short name")}>
        {/* The short name is in every permit's URL and its number, so changing
            it would break links that already exist. */}
        <Input value={template.short_name} disabled />
      </Form.Item>

      <Form.Item name="view_type" label={t("How sections are shown")}>
        <Select
          options={[
            { value: "Collapse", label: t("All at once, collapsible") },
            { value: "Step", label: t("One step at a time") },
          ]}
        />
      </Form.Item>

      <Space wrap>
        <Text type="secondary">{t("Type")}:</Text>
        <Tag>{template.template_type_name ?? t("No type")}</Tag>
        {template.admins_name && (
          <>
            <Text type="secondary">{t("Administered by")}:</Text>
            <Tag>{template.admins_name}</Tag>
          </>
        )}
      </Space>

      <div style={{ marginTop: 16 }}>
        <Button type="primary" loading={savingDetails} onClick={saveDetails}>
          {t("Save")}
        </Button>
      </div>
    </Form>
  );

  const formsPanel = (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => window.open(designerFormUrl(0), "_blank")}
        >
          {t("Add a section")}
        </Button>
        <Text type="secondary">
          {t("Sections are the forms people fill in on a permit.")}
        </Text>
      </Space>

      {sections.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("No sections yet. Add the first one to get started.")}
        />
      ) : (
        <List
          size="small"
          dataSource={sections}
          renderItem={(section: any) => (
            <List.Item
              actions={[
                <Button
                  key="edit"
                  size="small"
                  icon={<FormOutlined />}
                  onClick={() => navigate(`/forms/${section.id}/design`)}
                >
                  {t("Edit")}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={t(section.title)}
                description={
                  <Space size={6}>
                    <Tag>{section.type}</Tag>
                    {!section.stage && <Tag color="warning">{t("Not in any stage")}</Tag>}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const openDocumentModal = (document: TemplateDocument | null) => {
    setEditing(document);
    documentForm.resetFields();
    documentForm.setFieldsValue({ name: document?.name ?? "", file: undefined });
    setDocumentModalOpen(true);
  };

  const saveDocument = async () => {
    const values = await documentForm.validateFields();
    const file: Blob | undefined = values.file?.[0]?.originFileObj;
    setSavingDocument(true);
    try {
      if (editing) {
        // A missing file means "keep the one you have" — renaming shouldn't
        // force the administrator to re-upload the .docx.
        await ApiService.apiMainTemplateDocumentsPartialUpdate(editing.id, {
          name: values.name,
          ...(file ? { file } : {}),
        });
      } else {
        await ApiService.apiMainTemplateDocumentsCreate({
          template: template.id,
          name: values.name,
          file: file!,
        });
      }
      message.success(t("Saved"));
      setDocumentModalOpen(false);
      await load();
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
    setSavingDocument(false);
  };

  const removeDocument = async (document: TemplateDocument) => {
    try {
      await ApiService.apiMainTemplateDocumentsDestroy(document.id);
      message.success(t("Removed"));
      await load();
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
  };

  const moveDocument = async (index: number, delta: number) => {
    const reordered = [...documents];
    const target = index + delta;
    if (target < 0 || target >= reordered.length) return;
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    // Optimistic: the list is short and the round trip is otherwise visible as
    // a jump. `load()` reconciles.
    setDocuments(reordered);
    try {
      await ApiService.apiMainTemplateDocumentsReorder({
        template: template.id,
        ids: reordered.map((d) => d.id),
      });
      await load();
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
      await load();
    }
  };

  const documentsPanel = (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openDocumentModal(null)}
        >
          {t("Add a document")}
        </Button>
        <Text type="secondary">
          {t(
            "Each document is a Word file with the permit's data filled in. Its name is what people pick in the Actions menu."
          )}
        </Text>
      </Space>

      {!setup.pdf_available && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t("This server can only produce DOCX.")}
          description={t(
            "PDF conversion needs LibreOffice installed on the server. Until it is, the Actions menu offers DOCX only."
          )}
        />
      )}

      {documents.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t(
            "No documents yet. Permits of this type cannot be printed."
          )}
        />
      ) : (
        <List
          size="small"
          dataSource={documents}
          renderItem={(document, index) => (
            <List.Item
              actions={[
                <Tooltip key="up" title={t("Move up")}>
                  <Button
                    size="small"
                    type="text"
                    disabled={index === 0}
                    icon={<ArrowUpOutlined />}
                    onClick={() => moveDocument(index, -1)}
                  />
                </Tooltip>,
                <Tooltip key="down" title={t("Move down")}>
                  <Button
                    size="small"
                    type="text"
                    disabled={index === documents.length - 1}
                    icon={<ArrowDownOutlined />}
                    onClick={() => moveDocument(index, 1)}
                  />
                </Tooltip>,
                <Button
                  key="edit"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openDocumentModal(document)}
                >
                  {t("Edit")}
                </Button>,
                <Popconfirm
                  key="remove"
                  title={t("Remove this document?")}
                  description={t(
                    "It stops being offered on new permits. Permits already printed from it are unaffected."
                  )}
                  okText={t("Remove")}
                  cancelText={t("Cancel")}
                  onConfirm={() => removeDocument(document)}
                >
                  <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={<FileTextOutlined />}
                title={document.name}
                description={
                  <Space size={6}>
                    <Text type="secondary">{document.file_name}</Text>
                    {index === 0 && documents.length > 1 && (
                      // The first document is the one EDS signs and the one the
                      // older links resolve to, so it is worth naming.
                      <Tag color="blue">{t("Primary")}</Tag>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}

      <Modal
        open={documentModalOpen}
        title={editing ? t("Edit document") : t("Add a document")}
        okText={t("Save")}
        cancelText={t("Cancel")}
        confirmLoading={savingDocument}
        onOk={saveDocument}
        onCancel={() => setDocumentModalOpen(false)}
        destroyOnClose
      >
        <Form form={documentForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("Name")}
            rules={[{ required: true, message: t("A name is required.") }]}
          >
            <Input placeholder={t("e.g. Permit to Work")} />
          </Form.Item>
          <Form.Item
            name="file"
            label={t("Word file (.docx)")}
            valuePropName="fileList"
            getValueFromEvent={(e: any) =>
              Array.isArray(e) ? e : e?.fileList?.slice(-1)
            }
            extra={
              editing
                ? t("Leave empty to keep the current file.")
                : t("The Word template, with placeholders where the data goes.")
            }
            rules={
              editing
                ? []
                : [{ required: true, message: t("A .docx file is required.") }]
            }
          >
            <Upload
              accept=".docx"
              maxCount={1}
              // Uploaded with the form, not on drop: the name is part of the
              // same create call.
              beforeUpload={() => false}
              listType="text"
            >
              <Button icon={<UploadOutlined />}>{t("Choose a file")}</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  const processPanel = (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Text type="secondary">
        {t(
          "The process is the order sections are filled and approvals are collected."
        )}
      </Text>

      {counts.sections === 0 ? (
        <Alert
          type="info"
          showIcon
          message={t("Add a section first — there is nothing to put in a stage yet.")}
        />
      ) : (
        unplaced > 0 && (
          <Alert
            type="warning"
            showIcon
            message={t(
              "{{count}} sections are not in any stage, so a permit never reaches them.",
              { count: unplaced }
            )}
          />
        )
      )}

      <Space wrap>
        <Tag>{t("{{count}} stages", { count: counts.stages })}</Tag>
        <Tag>{t("{{count}} sign-offs", { count: counts.workflows })}</Tag>
        <Tag>{t("{{count}} statuses", { count: counts.statuses })}</Tag>
      </Space>

      <div>
        <Button
          type="primary"
          icon={<ArrowRightOutlined />}
          onClick={() => navigate(`/templates/${template.id}/process`)}
        >
          {t("Open the process designer")}
        </Button>
      </div>
    </Space>
  );

  const publishPanel = (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {publishedVersion == null ? (
        <Alert
          type="warning"
          showIcon
          message={t("This template's process has never been published.")}
          description={t(
            "Until it is, permits raised from this template have no process to follow. Publishing happens in the process designer."
          )}
        />
      ) : (
        <Alert
          type="success"
          showIcon
          message={t("Published — version {{version}}", {
            version: publishedVersion,
          })}
          description={t(
            "Permits created from now on use this version. Ones already in flight keep the version they started on."
          )}
        />
      )}

      <Space wrap>
        <Text type="secondary">
          {t("{{count}} permits have been raised from this template.", {
            count: counts.requests,
          })}
        </Text>
      </Space>

      <Space wrap>
        <Button
          onClick={() => navigate(`/templates/${template.id}/process`)}
        >
          {t("Open the process designer")}
        </Button>
        {publishedVersion != null && (
          <Button
            type="primary"
            onClick={() => navigate(`/requests/${template.short_name}/create`)}
          >
            {t("Raise a test permit")}
          </Button>
        )}
      </Space>
    </Space>
  );

  const panels: Record<string, JSX.Element> = {
    details: detailsPanel,
    forms: formsPanel,
    documents: documentsPanel,
    process: processPanel,
    publish: publishPanel,
  };

  return (
    <Spin spinning={loading}>
      <PageHeader
        title={t(template.name)}
        meta={<Tag style={{ marginInlineEnd: 0 }}>{template.short_name}</Tag>}
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <SetupProgress steps={steps} activeKey={step} onSelect={setStep} />
      </Card>

      <Card size="small">{panels[step] ?? detailsPanel}</Card>
    </Spin>
  );
};

export default TemplateSetupPage;
